import { apiErrorMessage, useAuth } from '@/lib'
import { Button, SelectField, TextField } from '@/ui'
import {
	type AddressInputDataCountry,
	type BillingProfileData,
	type InvoiceLocale,
	invoiceLocale,
	profilesIndexQueryKey,
	updateBillingDetailsDataSchema,
	useBillingCountryIndex,
	useOrganisationRelationShow,
	useProfilesStore,
	useProfilesUpdateBillingDetails,
} from '@jobilla/nexus-sdk'
import { useForm } from '@tanstack/react-form'
import { useQueryClient } from '@tanstack/react-query'
import { useEffect, useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { z } from 'zod'

// The generated schema misses that Required rejects empty strings, and the inputs always hold strings.
// TODO ENG-5750: drop these overrides once the codegen emits the backend's constraints.
function billingDetailsSchema(messages?: { required: string; invalidEmail: string }) {
	const required = z.string().trim().min(1, messages?.required)

	return updateBillingDetailsDataSchema.extend({
		name: required,
		email: required.email(messages?.invalidEmail),
		address: updateBillingDetailsDataSchema.shape.address.extend({
			line1: required,
			line2: z.string(),
			postalCode: required,
			city: z.string(),
			state: z.string(),
			country: required,
		}),
		vatId: z.string(),
	})
}

const invoiceLocales = Object.values(invoiceLocale)

// Each language is named in itself, so readers find their own language regardless of the UI language.
export function invoiceLocaleName(locale: InvoiceLocale): string {
	const language = locale.split('-')[0]

	const name = new Intl.DisplayNames([language], { type: 'language' }).of(language) ?? locale

	return name.charAt(0).toLocaleUpperCase(language) + name.slice(1)
}

function billingDetailsValues(profile: BillingProfileData | null, fallback: { name?: string; email?: string }) {
	const locale: InvoiceLocale = profile?.locale ?? invoiceLocale['en-GB']

	return {
		name: profile?.name ?? fallback.name ?? '',
		email: profile ? (profile.email ?? '') : (fallback.email ?? ''),
		address: {
			line1: profile?.address?.line1 ?? '',
			line2: profile?.address?.line2 ?? '',
			postalCode: profile?.address?.postalCode ?? '',
			city: profile?.address?.city ?? '',
			// Not editable here, but kept so saving does not wipe it on Stripe.
			state: profile?.address?.state ?? '',
			country: profile?.address?.country?.toUpperCase() ?? '',
		},
		vatId: profile?.taxIds.find((taxId) => taxId.type === 'eu_vat')?.value ?? '',
		locale,
	}
}

export function hasCompleteBillingDetails(profile: BillingProfileData): boolean {
	return billingDetailsSchema().safeParse(billingDetailsValues(profile, {})).success
}

interface BillingDetailsFormProps {
	organisationId: string
	/** Null creates a new profile. */
	profile: BillingProfileData | null
	submitLabel: string
	onSaved: (profile: BillingProfileData) => void
	onCancel?: () => void
}

export function BillingDetailsForm(
	{ organisationId, profile, submitLabel, onSaved, onCancel }: BillingDetailsFormProps,
) {
	const { t, i18n } = useTranslation('common')
	const queryClient = useQueryClient()
	const updateMutation = useProfilesUpdateBillingDetails()
	const createMutation = useProfilesStore()
	const saveError = profile ? updateMutation.error : createMutation.error

	const { data: countries } = useBillingCountryIndex()
	const isEuCountry = (code: string) => !!countries?.some((country) => country.code === code && country.eu)

	const countryCodes = useMemo(() => (countries ?? []).map((country) => country.code as string), [countries])
	const countryNames = useMemo(() => new Intl.DisplayNames([i18n.language], { type: 'region' }), [i18n.language])

	const schema = useMemo(
		() =>
			billingDetailsSchema({
				required: t('billingDetails.required'),
				invalidEmail: t('billingDetails.invalidEmail'),
			}),
		[t],
	)

	const { user } = useAuth()
	const { data: organisation } = useOrganisationRelationShow(organisationId, {
		enabled: !!organisationId && !profile,
	})

	const form = useForm({
		defaultValues: billingDetailsValues(profile, { name: organisation?.name, email: user?.email }),
		validators: { onChange: schema },
		onSubmit: async ({ value }) => {
			const data = {
				...value,
				address: {
					...value.address,
					line2: value.address.line2.trim() || null,
					city: value.address.city.trim() || null,
					state: value.address.state.trim() || null,
					// The select only offers codes from the billing-countries endpoint, so the string is one of the enum.
					country: value.address.country as AddressInputDataCountry,
				},
				vatId: isEuCountry(value.address.country) ? value.vatId.trim() || null : undefined,
			}

			let updated: BillingProfileData

			try {
				updated = profile
					? await updateMutation.mutateAsync({ organisation: organisationId, profile: profile.id, data })
					: await createMutation.mutateAsync({ organisation: organisationId, data })
			} catch {
				// shown from the mutation's error state
				return
			}

			await queryClient.invalidateQueries({ queryKey: profilesIndexQueryKey(organisationId) })

			onSaved(updated)
		},
	})

	useEffect(() => {
		if (!profile && organisation?.name && !form.getFieldValue('name')) {
			form.setFieldValue('name', organisation.name)
		}
	}, [form, organisation?.name, profile])

	return (
		<form
			onSubmit={(event) => {
				event.preventDefault()
				void form.handleSubmit()
			}}
			className="space-y-4"
		>
			<form.Subscribe>
				{({ canSubmit, isSubmitting, values }) => (
					<>
						<form.Field name="name">
							{({ state, handleChange, handleBlur }) => (
								<TextField
									label={t('billingDetails.name')}
									value={state.value}
									onChange={handleChange}
									onBlur={handleBlur}
									autocomplete="organization"
									isWaiting={isSubmitting}
									errors={state.meta.errors}
								/>
							)}
						</form.Field>
						<form.Field name="email">
							{({ state, handleChange, handleBlur }) => (
								<TextField
									label={t('billingDetails.email')}
									hint={t('billingDetails.emailHint')}
									type="email"
									value={state.value}
									onChange={handleChange}
									onBlur={handleBlur}
									autocomplete="email"
									isWaiting={isSubmitting}
									errors={state.meta.errors}
								/>
							)}
						</form.Field>
						<form.Field name="address.line1">
							{({ state, handleChange, handleBlur }) => (
								<TextField
									label={t('billingDetails.line1')}
									value={state.value}
									onChange={handleChange}
									onBlur={handleBlur}
									autocomplete="address-line1"
									isWaiting={isSubmitting}
									errors={state.meta.errors}
								/>
							)}
						</form.Field>
						<form.Field name="address.line2">
							{({ state, handleChange, handleBlur }) => (
								<TextField
									label={t('billingDetails.line2')}
									value={state.value}
									onChange={handleChange}
									onBlur={handleBlur}
									autocomplete="address-line2"
									isWaiting={isSubmitting}
								/>
							)}
						</form.Field>
						<div className="grid grid-cols-2 gap-4">
							<form.Field name="address.postalCode">
								{({ state, handleChange, handleBlur }) => (
									<TextField
										label={t('billingDetails.postalCode')}
										value={state.value}
										onChange={handleChange}
										onBlur={handleBlur}
										autocomplete="postal-code"
										isWaiting={isSubmitting}
										errors={state.meta.errors}
									/>
								)}
							</form.Field>
							<form.Field name="address.city">
								{({ state, handleChange, handleBlur }) => (
									<TextField
										label={t('billingDetails.city')}
										value={state.value}
										onChange={handleChange}
										onBlur={handleBlur}
										autocomplete="address-level2"
										isWaiting={isSubmitting}
									/>
								)}
							</form.Field>
						</div>
						<form.Field name="address.country">
							{({ state, handleChange }) => (
								<SelectField
									label={t('billingDetails.country')}
									options={countryCodes}
									value={state.value}
									onChange={handleChange}
									renderOption={(country) => countryNames.of(country) ?? country}
									placeholder={t('billingDetails.countryPlaceholder')}
									isWaiting={isSubmitting}
									errors={state.meta.errors}
								/>
							)}
						</form.Field>
						{isEuCountry(values.address.country) && (
							<form.Field name="vatId">
								{({ state, handleChange, handleBlur }) => (
									<TextField
										label={t('billingDetails.vatId')}
										hint={t('billingDetails.vatIdHint')}
										value={state.value}
										onChange={handleChange}
										onBlur={handleBlur}
										isWaiting={isSubmitting}
									/>
								)}
							</form.Field>
						)}
						<form.Field name="locale">
							{({ state, handleChange }) => (
								<SelectField
									label={t('billingDetails.invoiceLanguage')}
									hint={t('billingDetails.invoiceLanguageHint')}
									options={invoiceLocales}
									value={state.value}
									onChange={handleChange}
									renderOption={invoiceLocaleName}
									isWaiting={isSubmitting}
									errors={state.meta.errors}
								/>
							)}
						</form.Field>
						{saveError
							? (
								<p className="text-sm text-red-700">
									{apiErrorMessage(saveError) ?? t('billingDetails.saveFailed')}
								</p>
							)
							: null}
						<div className="flex justify-end gap-3">
							{onCancel
								? (
									<Button type="neutral" onClick={onCancel} disabled={isSubmitting}>
										{t('common.cancel')}
									</Button>
								)
								: null}
							<Button
								type="committal"
								submit={true}
								postfix="arrow"
								disabled={!canSubmit || isSubmitting}
								isLoading={isSubmitting}
							>
								{submitLabel}
							</Button>
						</div>
					</>
				)}
			</form.Subscribe>
		</form>
	)
}
